import {
  selectIsCampaignPerformanceAlertsFeatureFlagOn,
  selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
} from 'core/userContext/selectors';
import _compact from 'lodash/compact';
import { canAddExperiment, getHasSendTimeOptimization } from 'modules/campaigns/editor/utils';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { getLatestExperiment } from 'modules/campaigns/lib/utils/experiment';
import { CampaignState } from 'modules/domains/campaign';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { OverflowMenu } from 'ui/actionMenu';
import { StyledOptions } from 'ui/actionMenu/ActionMenu.styles';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { BUTTON_SIZES } from 'ui/button';
import { Analytics, ExperimentFlask, PerformanceAlerts16 } from 'ui/icons';
import { colors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { NoOp } from 'utils/noop';

import { getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils/campaign';
import { useExperimentTakeover } from 'modules/experiments/takeover';
import { useCampaignContext, useEditorContext, useTemplateContext } from '../../../../context';

export const CAMPAIGN_EDITOR_OVERFLOW_MENU_TEST_ID = 'campaign-editor-overflow-menu';

type ClickAttr = {
  onClick: NoOp;
};

type ExperimentAttribute = ClickAttr;

const OverflowMenuItem = styled.div`
  gap: ${spacing.half};
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const StyledOverflowMenu = styled(OverflowMenu)`
  ${StyledOptions} {
    width: ${pxToRem(188)};
  }
`;

const VIEW_ANALYTICS_OPTION_TEST_ID = 'view-analytics';
const CREATE_EXP_OPTION_TEST_ID = 'create-experiment';
export const VIEW_ALERTS_OPTION_TEST_ID = 'view-alerts';

const generateOptions = (
  campaignId: number,
  experimentAttribute: ExperimentAttribute,
  isExperimentButtonDisabled: boolean,
  isCampaignPerformanceAlertsFeatureEnabled: boolean,
  isCampaignPerformanceAlertsHistoryFeatureEnabled: boolean,
  onViewAlerts: () => void,
  isWhatsAppCampaign: boolean,
): ActionMenuOption[] => {
  return _compact([
    {
      id: VIEW_ANALYTICS_OPTION_TEST_ID,
      as: AllowedActionMenuElements.Link,
      content: (
        <OverflowMenuItem>
          <Analytics height={20} width={20} />
          View analytics
        </OverflowMenuItem>
      ),
      href: `/analytics/campaignPerformance?campaignId=${campaignId}`,
    },
    {
      id: CREATE_EXP_OPTION_TEST_ID,
      as: AllowedActionMenuElements.Link,
      isDisabled: isExperimentButtonDisabled,
      content: (
        <OverflowMenuItem>
          <ExperimentFlask height={20} width={20} />
          Create experiment
        </OverflowMenuItem>
      ),
      ...experimentAttribute,
    },
    isCampaignPerformanceAlertsFeatureEnabled &&
      isCampaignPerformanceAlertsHistoryFeatureEnabled &&
      !isWhatsAppCampaign && {
        as: AllowedActionMenuElements.Button,
        content: (
          <OverflowMenuItem>
            <PerformanceAlerts16 color={colors.iconSubtle} />
            View alerts
          </OverflowMenuItem>
        ),
        id: VIEW_ALERTS_OPTION_TEST_ID,
        onClick: onViewAlerts,
      },
  ]);
};

export const CampaignSummaryOverflowMenu = () => {
  const {
    campaign: { id, name, campaignState, experiments, messageMedium },
    campaign,
  } = useCampaignContext();
  const { openExperimentCreationModal } = useExperimentTakeover();
  const {
    templateState: { isDefaultTemplate, templateHasContentIssues },
    templateAndContent,
  } = useTemplateContext();
  const { hasLocales } = useLocaleSettingsContext();
  const isWhatsAppCampaign = getIsWhatsAppCampaign(messageMedium);
  const isExperimentButtonDisabled =
    isDefaultTemplate || templateHasContentIssues || isWhatsAppCampaign;
  const isCampaignPerformanceAlertsFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );
  const isCampaignPerformanceAlertsHistoryFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsHistoryFeatureFlagOn,
  );

  const experiment = useMemo(() => getLatestExperiment(experiments), [experiments]);

  // Experiment Modal Auto Opening

  const [templateDataResolved, setTemplateDataResolved] = useState(false);
  const { isExperimentModalOpen, setIsAlertHistorySlideOverOpen } = useEditorContext();
  const onViewAlerts = () => setIsAlertHistorySlideOverOpen(true);

  useEffect(() => {
    if (isExperimentModalOpen && templateDataResolved)
      openExperimentCreationModal({
        id,
        name,
        campaignState,
        isCampaignSTOEnabled: getHasSendTimeOptimization(campaign),
        hasLocales,
        isCampaignValid: !isExperimentButtonDisabled,
      });
  }, [isExperimentModalOpen, isExperimentButtonDisabled, hasLocales, templateDataResolved]);

  useEffect(() => {
    if (Object.keys(templateAndContent).length > 0) {
      setTemplateDataResolved(true);
    }
  }, [templateAndContent]);

  const experimentAttribute: ExperimentAttribute = {
    onClick: () =>
      openExperimentCreationModal({
        id,
        name,
        campaignState,
        isCampaignSTOEnabled: getHasSendTimeOptimization(campaign),
        hasLocales,
        isCampaignValid: true,
      }),
  };

  const options = generateOptions(
    id,
    experimentAttribute,
    isExperimentButtonDisabled,
    isCampaignPerformanceAlertsFeatureEnabled,
    isCampaignPerformanceAlertsHistoryFeatureEnabled,
    onViewAlerts,
    isWhatsAppCampaign,
  ).filter(({ id: _id }) => {
    /**
     * Running and finished campaigns currently don't need the "View analytics" options
     */
    if (
      (campaignState === CampaignState.Running || campaignState === CampaignState.Finished) &&
      _id === VIEW_ANALYTICS_OPTION_TEST_ID
    ) {
      return false;
    }

    if (_id === CREATE_EXP_OPTION_TEST_ID && canAddExperiment(campaign, experiment) === false) {
      return false;
    }

    return true;
  });

  return (
    <>
      {options.length > 0 && (
        <StyledOverflowMenu
          size={BUTTON_SIZES.LARGE}
          options={options}
          id={CAMPAIGN_EDITOR_OVERFLOW_MENU_TEST_ID}
          data-test={CAMPAIGN_EDITOR_OVERFLOW_MENU_TEST_ID}
        />
      )}
    </>
  );
};
